/**
 * Tarjeta móvil para una reubicación (campos alineados con la API).
 */
import { Badge } from '@shared/components/ui/badge';
import { RenderItemProps } from '@shared/components/ui/VirtualList';
import { RelocationModel, RelocationStatus } from '../../models/Relocation';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { faCartShopping, faEye } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export interface RelocationCardProps extends RenderItemProps<RelocationModel> {
  className?: string;
  selectedColumns?: string[];
  cartLabelsById?: Record<number, string>;
}

const statusBadgeClass = (status: RelocationStatus): string => {
  switch (status) {
    case 'draft':
      return 'text-badge-status-2 bg-card-border light:bg-badge-bg-status-2';
    case 'dispatched':
      return 'text-success bg-bg-success';
    case 'cancelled':
      return 'text-badge-status-4 bg-card-border light:bg-badge-bg-status-4';
    default:
      return 'text-subtext bg-card-border';
  }
};

export const Card = ({ item: row, className = '', selectedColumns, cartLabelsById = {} }: RelocationCardProps) => {
  const { t } = useTranslation('relocationsModule');
  const navigate = useNavigate();

  const isColumnVisible = (columnId: string) => {
    if (!selectedColumns) return true;
    return selectedColumns.includes(columnId);
  };

  const handleView = () => navigate(`/relocations/${row.id}`);
  const cartLabel = cartLabelsById[row.cart_id] || row.cart_id;

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div className={`py-4 px-2 border-b border-card-border hover:bg-hover-press transition-colors ${className}`}>
      <div className="space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            {isColumnVisible('status') && (
              <Badge variant="outline" className={`text-xs border-0 ${statusBadgeClass(row.status)}`}>
                {t(`filters.status_options.${row.status}`)}
              </Badge>
            )}
          </div>
          <button
            type="button"
            onClick={handleView}
            aria-label={t('actions.view')}
            title={t('actions.view')}
            className="inline-flex items-center justify-center w-8 h-8 rounded-md text-icons hover:text-title hover:bg-hover-press transition-colors"
            data-testid={`relocation-view-${row.id}`}
          >
            <FontAwesomeIcon icon={faEye} className="w-4 h-4" />
          </button>
        </div>

        {(isColumnVisible('cart_id') || isColumnVisible('location_order_id')) && (
          <div className="text-sm text-subtext space-y-1">
            {isColumnVisible('location_order_id') && (
              <div>
                <span className="text-complementary-text">{t('table.location_order_id')}: </span>
                <span className="text-text">{row.location_order_id ?? '-'}</span>
              </div>
            )}
            {isColumnVisible('cart_id') && (
              <div>
                <span className="text-complementary-text">{t('table.cart_id')}: </span>
                <span className="inline-flex items-center gap-1.5 text-text">
                  <FontAwesomeIcon icon={faCartShopping} className="w-3.5 h-3.5 text-icons" />
                  <span>{cartLabel}</span>
                </span>
              </div>
            )}
          </div>
        )}

        {(isColumnVisible('dispatched_at') || isColumnVisible('created_at')) && (
          <div className="flex flex-wrap gap-3 text-xs text-subtext">
            {isColumnVisible('created_at') && (
              <span>
                {t('table.created_at')}: {formatDate(row.created_at)}
              </span>
            )}
            {isColumnVisible('dispatched_at') && (
              <span>
                {t('table.dispatched_at')}: {formatDate(row.dispatched_at)}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
